import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { Store } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { from } from "rxjs";
import { catchError, exhaustMap, map, mergeMap, switchMap, tap } from "rxjs/operators";

import { PartiesService } from "src/app/shared/services/parties/parties.service";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import * as PartiesActionTypes from "../../actions/parties/parties.actions";
import * as ContractsActionTypes from "../../actions/contracts/contracts.actions";
import * as CardsActionTypes from "../../actions/cards/cards.actions";
import { AppState } from "../../reducers";
import { CountriesService } from "src/app/shared/services/countries/countries.service";
import { CategoriesService } from "src/app/shared/services/categories/categories.service";
import { PropertiesService } from "src/app/shared/services/properties/properties.service";
import { ContractsService } from "src/app/shared/services/contracts/contracts.service";
import { CardsService } from "src/app/shared/services/cards/cards.service";
import { VehiclesService } from "src/app/shared/services/vehicles/vehicles.service";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

@Injectable()
export class PartiesEffects {
  // Get Party Salutations
  getSalutations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getSalutations),
      exhaustMap((action) =>
        this.partiesService.getSalutations(action.pageReq).pipe(
          map((result) =>
            PartiesActionTypes.loadSalutations({ response: result })
          ),
          catchError((error) => {
            const actions = [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Customers.errorLoadSalutations",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Countries
  getCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getCountries),
      exhaustMap((action) =>
        this.countriesService.getCountries(action.pageReq).pipe(
          map((result) =>
            PartiesActionTypes.loadCountries({ response: result })
          ),
          catchError((error) => {
            const actions = [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Customers.errorLoadCountries",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Categories by Usage
  getCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getCategoriesByUsage),
      exhaustMap((action) =>
        this.categoriesService
          .getCategoriesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              PartiesActionTypes.loadCategoriesByUsage({ response: result })
            ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Customers.errorLoadCategories",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Optional Categories by Usage
  getOptionalCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getOptionalCategoriesByUsage),
      exhaustMap((action) =>
        this.categoriesService
          .getCategoriesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              PartiesActionTypes.loadOptionalCategoriesByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj("App.Errors.Error", SnackbarType.ERROR)
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Properties by Usage
  getProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getPropertiesByUsage),
      exhaustMap((action) =>
        this.propertiesService
          .getPropertiesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              PartiesActionTypes.loadPropertiesByUsage({ response: result })
            ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Cusomters.errorLoadProperties",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Optional Properties by Usage
  getOptionalProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getOptionalPropertiesByUsage),
      exhaustMap((action) =>
        this.propertiesService
          .getPropertiesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              PartiesActionTypes.loadOptionalPropertiesByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj("App.Errors.Error", SnackbarType.ERROR)
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Create Customer Party
  createCustomerParty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.createCustomerParty),
      exhaustMap((action) =>
        this.partiesService.createCustomersParty(action).pipe(
          mergeMap((result) => [
            PartiesActionTypes.resetCreation(),
            PartiesActionTypes.getCustomerParty({
              partyCode: result?.result?.code,
            })
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Customers.customerCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ?? "Customers.customerCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Update Customer Party
  updateCustomerParty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.updateCustomerParty),
      exhaustMap((action) =>
        this.partiesService.updateCustomersParty(action).pipe(
          map((result) => {
            return PartiesActionTypes.getCustomerParty({
              partyCode: result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Customers.customerUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ?? "Customers.customerUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Customer
  getCustomerParty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getCustomerParty),
      exhaustMap((action) =>
        this.partiesService.getCustomersParty(action).pipe(
          mergeMap((result) => [
            PartiesActionTypes.setCustomerParty({ party: result }),
            PartiesActionTypes.setLoadingEndState(),
          ]),
          catchError((error) => {
            const actions = [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ?? "Customers.errorLoadCustomerParty",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  //Customer Party List
  getCustomerPartyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PartiesActionTypes.getCustomerPartyList,
        PartiesActionTypes.getCustomerPartyListSearch
      ),
      switchMap((action) =>
        this.partiesService
          .getCustomerPartyList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
            PartiesActionTypes.loadCustomerPartyList({ response: result })
          ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.loadCustomerPartyList({
                  response: { result: [] } as any,
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Customers.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Categories for the list by Usage
  getCategoriesForList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getCategoriesForListByUsage),
      exhaustMap((action) =>
        this.categoriesService
          .getCategoriesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              PartiesActionTypes.loadCategoriesForListByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Customers.errorLoadCategoriesForList",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Properties for the list by Usage
  getPropertiesForList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getPropertiesForListByUsage),
      exhaustMap((action) =>
        this.propertiesService
          .getPropertiesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              PartiesActionTypes.loadPropertiesForListByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Customers.errorLoadPropertiesForList",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Supplier
  getSupplierParty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getSupplierParty),
      exhaustMap((action) =>
        this.partiesService.getSuppliersParty(action).pipe(
          mergeMap((result) => [
            PartiesActionTypes.setSupplierParty({ party: result }),
            PartiesActionTypes.setLoadingEndState(),
          ]),
          catchError((error) => {
            const actions = [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ?? "Customers.errorLoadSupplierParty",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Create Supplier Party
  createSupplierParty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.createSupplierParty),
      exhaustMap((action) =>
        this.partiesService.createSuppliersParty(action).pipe(
          mergeMap((result) => [
            PartiesActionTypes.resetCreation(),
            PartiesActionTypes.getSupplierParty({
              partyCode: result?.result?.code,
            })
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Suppliers.supplierCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ?? "Suppliers.supplierCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Update Supplier Party
  updateSupplierParty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.updateSupplierParty),
      exhaustMap((action) =>
        this.partiesService.updateSuppliersParty(action).pipe(
          map((result) => {
            return PartiesActionTypes.getSupplierParty({
              partyCode: result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Suppliers.supplierUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ?? "Suppliers.supplierUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  //Supplier Party List
  getSupplierPartyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getSupplierPartyList),
      switchMap((action) =>
        this.partiesService
          .getSupplierPartyList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              PartiesActionTypes.loadSupplierPartyList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.loadSupplierPartyList({
                  response: { result: [] } as any,
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Suppliers.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Employee
  getEmployeeParty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getEmployeeParty),
      exhaustMap((action) =>
        this.partiesService.getEmployeesParty(action).pipe(
          mergeMap((result) => [
            PartiesActionTypes.setEmployeeParty({ party: result }),
            PartiesActionTypes.setLoadingEndState(),
          ]),
          catchError((error) => {
            const actions = [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ?? "Customers.errorLoadEmployeeParty",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Create Employee Party
  createEmployeeParty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.createEmployeeParty),
      exhaustMap((action) =>
        this.partiesService.createEmployeesParty(action).pipe(
          mergeMap((result) => [
            PartiesActionTypes.resetCreation(),
            PartiesActionTypes.getEmployeeParty({
              partyCode: result?.result?.code,
            })
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Employees.employeeCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ?? "Employees.employeeCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Update Employee Party
  updateEmployeeParty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.updateEmployeeParty),
      exhaustMap((action) =>
        this.partiesService.updateEmployeesParty(action).pipe(
          map((result) => {
            return PartiesActionTypes.getEmployeeParty({
              partyCode: result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Employees.employeeUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ?? "Employees.employeeUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  //Employee Party List
  getEmployeePartyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getEmployeePartyList),
      switchMap((action) =>
        this.partiesService
          .getEmployeePartyList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              PartiesActionTypes.loadEmployeePartyList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.loadEmployeePartyList({
                  response: { result: [] } as any,
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Employees.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Customer Contract List
  getCustomerContractsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getCustomerContractsList),
      exhaustMap((action) =>
        this.contractsService
          .getContractsList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.container
          )
          .pipe(
            map((result) => {
              return ContractsActionTypes.loadContractList({
                response: result,
              });
            }),
            catchError((error) => {
              const actions = [
                ContractsActionTypes.loadContractList({
                  response: { list: [], timestamp: new Date().getTime() },
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Contracts.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Customer Card List
  getCustomerVehiclesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getCustomerVehiclesList),
      exhaustMap((action) =>
        this.vehicleService
          .getVehiclesList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.container
          )
          .pipe(
            map((result) => {
              return PartiesActionTypes.loadCustomerVehiclesList({
                response: result,
              });
            }),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Vehicles.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getCustomerEmployeesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getCustomerEmployeesList),
      exhaustMap((action) =>
        this.partiesService
          .getCustomerEmployeeList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.container
          )
          .pipe(
            map((result) => {
              return PartiesActionTypes.loadCustomerEmployeesList({
                response: result,
              });
            }),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Employees.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getCustomersExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getCustomerExcelList),
      exhaustMap((action) =>
        this.partiesService
          .getCustomerPartyList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              PartiesActionTypes.loadCustomerExcelList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.loadCustomerExcelList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Customers.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getEmployeesExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getEmployeeExcelList),
      exhaustMap((action) =>
        this.partiesService
          .getEmployeePartyList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              PartiesActionTypes.loadEmployeeExcelList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.loadEmployeeExcelList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Employees.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getSuppliersExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getSupplierExcelList),
      exhaustMap((action) =>
        this.partiesService
          .getSupplierPartyList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              PartiesActionTypes.loadSupplierExcelList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.loadSupplierExcelList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Suppliers.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Customers Table Config
  getCustomersTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getCustomerTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(
            action.options.code,
            action.options.settingType
          )
          .pipe(
            map((result) =>
              PartiesActionTypes.loadCustomerTableConfig({ response: result })
            ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.loadCustomerTableConfig({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Customers Table Config
  saveCustomersTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.saveCustomerTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveSuccess",
                  SnackbarType.SUCCESS
                )
              )
            ]
          ),
          catchError((error: any) => {
            const actions = [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Employees Table Config
  getEmployeesTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getEmployeeTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(
            action.options.code,
            action.options.settingType
          )
          .pipe(
            map((result) =>
              PartiesActionTypes.loadEmployeeTableConfig({ response: result })
            ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.loadEmployeeTableConfig({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Employees Table Config
  saveEmployeesTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.saveEmployeeTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveSuccess",
                  SnackbarType.SUCCESS
                )
              )
            ]
          ),
          catchError((error: any) => {
            const actions = [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Suppliers Table Config
  getSuppliersTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.getSupplierTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(
            action.options.code,
            action.options.settingType
          )
          .pipe(
            map((result) =>
              PartiesActionTypes.loadSupplierTableConfig({ response: result })
            ),
            catchError((error) => {
              const actions = [
                PartiesActionTypes.loadSupplierTableConfig({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Suppliers Table Config
  saveSuppliersTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartiesActionTypes.saveSupplierTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveSuccess",
                  SnackbarType.SUCCESS
                )
              )
            ]
          ),
          catchError((error: any) => {
            const actions = [
              PartiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private partiesService: PartiesService,
    private countriesService: CountriesService,
    private categoriesService: CategoriesService,
    private propertiesService: PropertiesService,
    private translate: TranslateService,
    private contractsService: ContractsService,
    private vehicleService: VehiclesService,
    private tableConfigService: TableConfigService
  ) {}

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
